import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  NotificationStage,
  NotificationTemplate,
} from '../../../../interfaces/delivery-company/orders';
import { emptyNotificationTemplate } from '../../../../data/notifications';
import {
  FormControl,
  TextField,
  Button,
  Menu,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  useTheme,
  styled,
  Alert,
} from '@mui/material';
import InputWithLegend from '../../../common/input-with-legend/input-with-legend';
import MarkUnreadChatAltIcon from '@mui/icons-material/MarkUnreadChatAlt';
import {
  formatMessageWithLineBreaks,
  getInvalidTemplateVars,
  ALL_STAGES,
  templateVariables,
} from './utils';
import CustomDialog from '../../../common/custom-dialog/custom-dialog';
import { AuthContext } from '../../../../contexts/user-context/user-context';

const PhonePreview = styled('div')(
  ({ theme, color }: { theme: any; color: string }) => ({
    width: '100%',
    maxWidth: 360,
    height: 640,
    margin: 'auto',
    background: '#ffffff',
    borderRadius: 36,
    border: `6px solid ${color}`,
    boxShadow: '0px 10px 40px rgba(0, 0, 0, 0.2)',
    padding: 15,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    overflow: 'hidden',
  })
);

interface TemplateDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (template: NotificationTemplate) => void;
  initialData: NotificationTemplate | null;
  previewContent: string;
  onContentChange: (content: string) => void;
  logoUrl?: string;
  color: string;
  stages?: NotificationStage[];
  singleTemplateMode?: boolean;
}

const TemplateDialog: React.FC<TemplateDialogProps> = ({
  open,
  onClose,
  onSave,
  initialData,
  previewContent,
  onContentChange,
  logoUrl,
  color,
  stages = ALL_STAGES,
  singleTemplateMode = false,
}) => {
  const textFieldRef = useRef<HTMLInputElement>(null);
  const [name, setName] = useState(initialData?.name || '');
  const [selectedStages, setSelectedStages] = useState<NotificationStage[]>(
    initialData
      ? (Object.keys(initialData.orderWithinStages) as NotificationStage[])
      : singleTemplateMode ? stages : []
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleStageChange = (stage: NotificationStage) => {
    setSelectedStages((prev) =>
      prev.includes(stage) ? prev.filter((s) => s !== stage) : [...prev, stage]
    );
  };

  const { user, config } = useContext(AuthContext);

  const handleMenuItemClick = (code: string) => {
    if (textFieldRef.current) {
      const textField = textFieldRef.current;
      const startPos = textField.selectionStart || 0;
      const endPos = textField.selectionEnd || 0;
      const newTemplate =
        previewContent.substring(0, startPos) +
        code +
        previewContent.substring(endPos);
      onContentChange(newTemplate);

      // Move cursor after inserted text
      const newCursorPosition = startPos + code.length;
      setTimeout(() => {
        textField.focus();
        textField.setSelectionRange(newCursorPosition, newCursorPosition);
      }, 0);
    }
    handleMenuClose();
  };

  const handleSubmit = () => {
    if (invalidVars.length) return;
    if (selectedStages.length === 0) {
      alert('Please select at least one stage.');
      return;
    }

    const orderWithinStages: Partial<Record<NotificationStage, number>> = {};
    selectedStages.forEach((stage) => {
      orderWithinStages[stage] = initialData?.orderWithinStages
        ? initialData?.orderWithinStages[stage]
        : 0;
    });

    onSave({
      name,
      template: previewContent,
      orderWithinStages,
    });
  };

  const invalidVars = getInvalidTemplateVars(previewContent, selectedStages);

  return (
    <CustomDialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        {initialData ?  'Edit Template' : 'New Template'}
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {!singleTemplateMode && <TextField
              autoComplete="off"
              label="Template Name"
              fullWidth
              margin="normal"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
            />}

            {!singleTemplateMode && (
              <>
                <Typography variant="subtitle1" sx={{ marginTop: 2 }}>
                  Select Applicable Stages
                </Typography>

                {invalidVars.length > 0 && (
                  <Alert severity="error">
                    The following custom values are not allowed with the selected
                    stages. Please remove them or modify the stage selection:
                    <br />
                    <br />
                    {invalidVars.map((item) => (
                      <li key={item.code}>
                        <b>{item.label}:</b> {item.code}
                      </li>
                    ))}
                  </Alert>
                )}
                <div style={{ display: 'flex' }}>
                  {stages.map((stage) => (
                    <div key={stage} style={{ marginTop: 8 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedStages.includes(stage)}
                            onChange={() => handleStageChange(stage)}
                          />
                        }
                        label={stage}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
            <TextField
              autoComplete="off"
              label="Template Content"
              fullWidth
              multiline
              rows={6}
              required
              margin="normal"
              value={previewContent}
              onChange={(e) => onContentChange(e.target.value)}
              placeholder="Enter your template content here..."
              inputRef={textFieldRef}
              InputProps={{
                endAdornment: (
                  <div style={{ position: 'absolute', bottom: 1, right: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      aria-controls="actions-menu"
                      aria-haspopup="true"
                      onClick={handleMenuOpen}
                    >
                      Insert Custom Info
                    </Button>
                    <Menu
                      id="actions-menu"
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={handleMenuClose}
                    >
                      {templateVariables
                        .filter((item) => {
                          let ok = true;
                          selectedStages.forEach((stage) => {
                            ok = ok && item.stages.includes(stage);
                          });
                          return ok;
                        })
                        .map((item) => (
                          <MenuItem
                            key={item.label}
                            onClick={() => handleMenuItemClick(item.code)}
                          >
                            {item.label}
                          </MenuItem>
                        ))}
                    </Menu>
                  </div>
                ),
              }}
              sx={{
                '& .MuiInputBase-root': {
                  paddingBottom: '40px', // Make space for the button
                  position: 'relative', // Needed for absolute positioning of button
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
              Preview
            </Typography>
            <PhonePreview color={color} theme={useTheme()}>
              <div
                style={{
                  position: 'absolute',
                  top: 10,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 60,
                  height: 6,
                  borderRadius: 10,
                }}
              ></div>
              <div
                style={{
                  backgroundColor: '#f5f5f5',
                  padding: 0,
                  borderRadius: 20,
                  height: '100%',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-end',
                }}
              >
                <div
                  style={{
                    padding: 0,
                    backgroundColor: '#dcf8c6',
                    borderRadius: 18,
                    maxWidth: '80%',
                    marginBottom: '10px',
                    alignSelf: 'flex-end',
                    wordWrap: 'break-word',
                  }}
                >
                  {logoUrl && (
                    <img
                      src={logoUrl}
                      alt="Organization logo"
                      style={{
                        width: '100%',
                        height: 'auto',
                        objectFit: 'cover',
                        borderRadius: '18px 18px 0 0',
                        background: 'white',
                      }}
                    />
                  )}
                  <div style={{ padding: '12px 16px', color: '#000' }}>
                    {formatMessageWithLineBreaks(
                      previewContent,
                      user?.organisation?.org_id ?? ''
                    ) || 'Your message will appear here...'}
                  </div>
                </div>
              </div>
            </PhonePreview>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="error">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={
            invalidVars.length > 0 ||
            (!singleTemplateMode && name.length == 0) ||
            previewContent.length === 0
          }
        >
          Save
        </Button>
      </DialogActions>
    </CustomDialog>
  );
};

export default TemplateDialog;
